/*
 * Zayian Designs — Custom Brass Bistro Shelf Builder (UI controller)
 * Loaded as an ES module only on pages that contain the bistro builder section.
 * Shares the look of the console builder (zd-console-builder.css) and its Three.js build.
 * Three.js and the 3D viewer are imported on demand; the page stays usable without WebGL.
 */
import * as core from './zd-bistro-core.js?v=3';
import { PRICING, shelfSurcharge, panelCount } from './zd-bistro-pricing.js?v=3';

const STORAGE_KEY = 'zd-bistro-builder:v1';
const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const safeStorage = {
  get(k) { try { return localStorage.getItem(k); } catch (e) { return null; } },
  set(k, v) { try { localStorage.setItem(k, v); } catch (e) { /* private mode */ } },
  del(k) { try { localStorage.removeItem(k); } catch (e) { /* ignore */ } }
};
const debounce = (fn, ms) => { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); }; };
const IN = (n) => n * core.MM_PER_IN;
const r2 = (n) => Math.round(n * 100) / 100;

const ICON = {
  rotate: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20 12a8 8 0 1 1-2.3-5.7M20 4v5h-5"/></svg>',
  dims: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 8h18M3 5v6M21 5v6M12 21V13M9 14l3-3 3 3"/></svg>',
  full: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5"/></svg>',
  png: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 4v11M7 10l5 5 5-5M5 20h14"/></svg>',
  pin: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 9h14M5 15h14M9 5v14"/></svg>',
  scene: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 20h18M5 20V9l5-4v15M10 20V5l6 3v12M16 20V8"/></svg>',
  minus: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 12h12"/></svg>',
  plus: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 12h12M12 6v12"/></svg>',
  check: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12l5 5 9-10"/></svg>'
};
const PLACEHOLDER = '<span class="zd-thumb__ph" aria-hidden="true"></span>';

export class BistroBuilder {
  constructor(root) {
    this.root = root;
    const cfgEl = root.querySelector('script[data-zd-config]');
    let override = {};
    try { override = JSON.parse(cfgEl?.textContent || '{}'); } catch (e) { console.error('[zd-bistro] invalid config JSON', e); }
    if (Array.isArray(override.finishes)) override.finishes = override.finishes.filter((f) => f && f.id && f.availability !== 'hidden');
    this.config = core.mergeConfig(core.DEFAULT_CONFIG, override);
    const titleCents = (t) => { const m = /([\d,]+(?:\.\d{1,2})?)/.exec(String(t || '')); return m ? Math.round(parseFloat(m[1].replace(/,/g, '')) * 100) : null; };
    this.config.pricing.ladder = (this.config.pricing.ladder || []).map((v) => ({ ...v, shopCents: titleCents(v.title) })).filter((v) => v.shopCents && v.available !== false);
    this.index = core.buildCatalogIndex(this.config);
    this.currency = { shop: this.config.pricing.currency || 'USD', active: (window.Shopify && Shopify.currency && Shopify.currency.active) || this.config.pricing.currency || 'USD', rate: Number((window.Shopify && Shopify.currency && Shopify.currency.rate) || 1) };
    this.sid = root.dataset.sectionId || 'zd';
    this.raw = {};
    this.viewer = null;
    this.viewerMode = 'loading';
    this.busy = false;
    this.qty = 1;
    this.notesText = '';
    this.thumbs = {};
    this.restore();
    this.render();
    this.bind();
    this.update({ initial: true });
    this.initViewer();
  }

  /* ============================================================ state */
  restore() {
    const shared = new URLSearchParams(location.search).get('design');
    this.notice = null;
    if (shared) {
      const r = core.decodeShare(shared, this.config);
      if (r) { this.state = r.state; this.notice = { text: r.changed ? 'Loaded a shared design. Some options were no longer available and were reset.' : 'Loaded a shared design.', kind: 'info' }; return; }
      this.notice = { text: 'That design link could not be read, so we started a new design.', kind: 'warn' };
    }
    const saved = safeStorage.get(STORAGE_KEY);
    if (saved) {
      try { this.state = core.sanitizeState(JSON.parse(saved), this.config).state; if (!this.notice) this.notice = { text: 'We restored your last design.', kind: 'info', reset: true }; return; } catch (e) { /* ignore */ }
    }
    this.state = core.defaultState(this.config);
  }
  save = debounce(() => safeStorage.set(STORAGE_KEY, JSON.stringify(core.compactState(this.state))), 250);

  /* ============================================================ render (once) */
  render() {
    const C = this.config;
    const id = (x) => `zd-${this.sid}-${x}`;
    this.ids = id;
    const card = (name, value, title, body, opts = {}) => `
      <label class="zd-card${opts.cls ? ' ' + opts.cls : ''}">
        <input type="radio" name="${id(name)}" value="${esc(value)}" data-field="${name}">
        <span class="zd-card__inner">
          ${opts.badge ? `<span class="zd-card__badge">${esc(opts.badge)}</span>` : ''}
          ${opts.art === false ? '' : `<span class="zd-card__art zd-thumb" data-thumb="${esc(opts.thumb || name + ':' + value)}">${opts.art || PLACEHOLDER}</span>`}
          <span class="zd-card__text"><span class="zd-card__title">${esc(title)}</span><span class="zd-card__price" data-delta="${name}:${esc(value)}"></span>${body ? `<span class="zd-card__body">${esc(body)}</span>` : ''}${opts.meta ? `<span class="zd-card__meta">${esc(opts.meta)}</span>` : ''}</span>
          <span class="zd-card__check" aria-hidden="true">${ICON.check}</span>
        </span>
      </label>`;
    this.cardHTML = card;
    const step = (n, key, title, intro, body) => `
      <section class="zd-step" id="${id('step-' + key)}" aria-labelledby="${id('step-' + key + '-h')}">
        <header class="zd-step__head">
          <span class="zd-step__num" aria-hidden="true">${n}</span>
          <div><h2 class="zd-step__title" id="${id('step-' + key + '-h')}"><span class="zd-visually-hidden">Step ${n}: </span>${title}</h2>
          <p class="zd-step__intro">${intro}</p></div>
          <span class="zd-step__value" data-summary="${key}" aria-hidden="true"></span>
        </header>
        <div class="zd-step__body">${body}</div>
      </section>`;
    const counter = (key, label, hint) => `
      <div class="zd-field" data-count="${key}">
        <span class="zd-field__label" id="${id(key + '-l')}">${label}</span>
        <div class="zd-row">
          <div class="zd-stepper zd-stepper--sm" role="group" aria-labelledby="${id(key + '-l')}">
            <button type="button" class="zd-stepper__btn" data-action="${key}-" aria-label="Fewer ${label.toLowerCase()}">${ICON.minus}</button>
            <output class="zd-qty__val" data-count-val="${key}" aria-live="polite"></output>
            <button type="button" class="zd-stepper__btn" data-action="${key}+" aria-label="More ${label.toLowerCase()}">${ICON.plus}</button>
          </div>
        </div>
        <p class="zd-field__hint" data-count-hint="${key}">${hint || ''}</p>
        <p class="zd-field__error" data-error="${key === 'lv' ? 'levels' : 'bays'}" role="alert" hidden></p>
      </div>`;
    const dimField = (k, label) => `
      <div class="zd-field" data-dim="${k}">
        <label class="zd-field__label" for="${id('dim-' + k)}"><span data-dim-label="${k}">${label}</span> <span class="zd-field__unit" data-unit-label></span></label>
        <div class="zd-stepper">
          <button type="button" class="zd-stepper__btn" data-step="${k}" data-dir="-1" aria-label="Decrease">${ICON.minus}</button>
          <input class="zd-input" id="${id('dim-' + k)}" type="text" inputmode="decimal" autocomplete="off" spellcheck="false" data-dim-input="${k}" aria-describedby="${id('dim-' + k + '-hint')} ${id('dim-' + k + '-err')}">
          <button type="button" class="zd-stepper__btn" data-step="${k}" data-dir="1" aria-label="Increase">${ICON.plus}</button>
        </div>
        <p class="zd-field__hint" id="${id('dim-' + k + '-hint')}" data-hint="${k}"></p>
        <p class="zd-field__error" id="${id('dim-' + k + '-err')}" data-error="${k}" role="alert" hidden></p>
      </div>`;
    const finishes = core.enabledFinishes(C);

    const steps = [
      step(1, 'model', 'Model', 'Three connector designs, each modelled in 3D on a Kinycraft bistro shelving design.', `
        <fieldset class="zd-fieldset"><legend class="zd-visually-hidden">Model</legend>
          <div class="zd-cards zd-cards--models">${core.MODELS.map((m) => card('model', m.id, m.name, m.text, { cls: m.hero ? 'zd-card--hero' : 'zd-card--secondary', badge: m.hero ? 'Signature design' : '', meta: `As on our ${m.ref}` })).join('')}</div>
          <p class="zd-field__error" data-error="model" role="alert" hidden></p>
        </fieldset>`),
      step(2, 'mounting', 'Mounting', 'How the frame is supported. Flanges, stays and fixings in the preview follow your choice.', `
        <fieldset class="zd-fieldset"><legend class="zd-visually-hidden">Mounting arrangement</legend>
          <div class="zd-cards zd-cards--2 zd-cards--compact" data-mounting-cards></div>
          <p class="zd-field__error" data-error="mounting" role="alert" hidden></p>
        </fieldset>
        <p class="zd-small zd-muted" data-mounting-note></p>
        <fieldset class="zd-fieldset zd-segmented" data-arms-wrap hidden><legend class="zd-label">Support arms</legend>
          <label><input type="radio" name="${id('arms')}" value="turned" data-field="arms"><span>Turned round arms</span></label>
          <label><input type="radio" name="${id('arms')}" value="flat" data-field="arms"><span>Flat arms</span></label>
        </fieldset>`),
      step(3, 'size', 'Dimensions', 'Standard Kinycraft sizes, or your own measurements. Decimals welcome.', `
        <div class="zd-row zd-row--between">
          <fieldset class="zd-fieldset zd-segmented"><legend class="zd-visually-hidden">Measurement units</legend>
            <label><input type="radio" name="${id('units')}" value="in" data-field="units"><span>Inches</span></label>
            <label><input type="radio" name="${id('units')}" value="cm" data-field="units"><span>Centimetres</span></label>
          </fieldset>
          <span class="zd-muted zd-small">Values convert exactly when you switch — prices never change with the unit.</span>
        </div>
        <fieldset class="zd-fieldset"><legend class="zd-label">Standard sizes <span class="zd-muted">(exact Kinycraft listing prices)</span></legend>
          <div class="zd-presets" data-preset-cards></div>
        </fieldset>
        <div class="zd-dims">
          ${dimField('W', 'Overall width')}
          ${dimField('H', 'Overall height')}
          ${dimField('D', 'Depth')}
        </div>
        <p class="zd-small zd-muted" data-size-note></p>`),
      step(4, 'levels', 'Shelf levels & bays', 'Levels are the shelf tiers; bays are the sections between posts across the width.', `
        <div class="zd-dims">
          ${counter('lv', 'Shelf levels', '')}
          ${counter('by', 'Bays', '')}
        </div>
        <p class="zd-small zd-muted" data-structure-note></p>
        <fieldset class="zd-fieldset zd-segmented" data-gap-wrap><legend class="zd-label">Shelf spacing</legend>
          <label><input type="radio" name="${id('gapmode')}" value="even" data-field="gapMode"><span>Evenly spaced</span></label>
          <label><input type="radio" name="${id('gapmode')}" value="custom" data-field="gapMode"><span>Custom spacing</span></label>
        </fieldset>
        <div class="zd-field" data-gap-field hidden>
          <label class="zd-field__label" for="${id('dim-G')}">Space between shelves <span class="zd-field__unit" data-unit-label></span></label>
          <div class="zd-stepper">
            <button type="button" class="zd-stepper__btn" data-step="G" data-dir="-1" aria-label="Decrease spacing">${ICON.minus}</button>
            <input class="zd-input" id="${id('dim-G')}" type="text" inputmode="decimal" autocomplete="off" spellcheck="false" data-dim-input="G" aria-describedby="${id('dim-G-err')}">
            <button type="button" class="zd-stepper__btn" data-step="G" data-dir="1" aria-label="Increase spacing">${ICON.plus}</button>
          </div>
          <p class="zd-field__hint">Measured centre to centre, starting from the lowest shelf. Custom spacing is made to order (priced as a custom configuration).</p>
          <p class="zd-field__error" id="${id('dim-G-err')}" data-error="gap" role="alert" hidden></p>
        </div>`),
      step(5, 'finish', 'Brass finish', 'One finish for every brass part — posts, connectors, rails, flanges and hammered brass shelves.', `
        <fieldset class="zd-fieldset"><legend class="zd-visually-hidden">Finish</legend>
          <div class="zd-cards zd-cards--finish">${finishes.map((f) => card('finish', f.id, f.name, '', { art: `<span class="zd-swatch__chip zd-swatch__chip--lg" style="--zd-swatch:${esc(f.color)}" aria-hidden="true"></span>` })).join('')}</div>
          <p class="zd-field__error" data-error="finish" role="alert" hidden></p>
        </fieldset>
        <div class="zd-finish-detail" data-finish-detail aria-live="polite"></div>`),
      step(6, 'shelves', 'Shelf panels', 'Would you like shelf panels included?', `
        <fieldset class="zd-fieldset"><legend class="zd-visually-hidden">Shelf panels</legend>
          <div class="zd-cards zd-cards--2 zd-cards--compact">
            ${card('shelves', 'included', 'Frame + Shelf Panels', 'Made-to-size panels in the material you choose below, delivered fitted to the frame.', { art: false })}
            ${card('shelves', 'none', 'Frame Only — No Shelf Panels', 'The complete brass frame with all support rails and hardware, ready for panels you source yourself.', { art: false })}
          </div>
        </fieldset>
        <p class="zd-notice zd-notice--info zd-small" data-frame-only-note hidden>Shelf panels are not included. The frame keeps every support rail and fixing, sized for panels you supply.</p>
        <div data-material-wrap>
          <p class="zd-label">Shelf material <span class="zd-muted" data-panel-count></span></p>
          <div class="zd-cards zd-cards--materials">
            ${core.SHELF_MATERIALS.map((m) => card('material', m.id, m.name, m.text, { cls: m.recommended ? 'zd-card--hero' : 'zd-card--secondary', badge: m.recommended ? 'Recommended' : '', thumb: 'material:' + m.id })).join('')}
          </div>
          <p class="zd-field__error" data-error="material" role="alert" hidden></p>
          <p class="zd-small zd-muted" data-material-note></p>
        </div>`),
      step(7, 'review', 'Review your design', 'Check every detail. Use Edit to change any selection.', `
        <dl class="zd-review" data-review></dl>
        <div class="zd-review-cols">
          <div><h3 class="zd-h3">Included</h3><ul class="zd-list zd-list--check" data-included></ul></div>
          <div><h3 class="zd-h3">Not included</h3><ul class="zd-list zd-list--x" data-excluded></ul></div>
        </div>
        <div class="zd-pricebox" data-pricebox></div>
        <ul class="zd-notes" data-warnings></ul>
        <div class="zd-field">
          <label class="zd-field__label" for="${id('notes')}">Notes for our workshop <span class="zd-muted">(optional)</span></label>
          <textarea class="zd-input zd-textarea" id="${id('notes')}" rows="3" maxlength="500" data-notes placeholder="Anything we should know — obstacles, tiles, appliance clearances…"></textarea>
        </div>
        <div class="zd-tools">
          <button type="button" class="zd-btn zd-btn--ghost" data-action="share">Copy share link</button>
          <button type="button" class="zd-btn zd-btn--ghost" data-action="print">Print summary</button>
          <button type="button" class="zd-btn zd-btn--ghost" data-action="png">Download image</button>
          <button type="button" class="zd-btn zd-btn--ghost zd-btn--danger" data-action="reset">Reset design</button>
        </div>
        <div class="zd-share" data-share hidden>
          <label class="zd-field__label" for="${id('share-url')}">Share link <span class="zd-muted">(contains design options only — no personal details)</span></label>
          <div class="zd-row"><input class="zd-input" id="${id('share-url')}" readonly data-share-url><button type="button" class="zd-btn zd-btn--small" data-action="copy">Copy</button></div>
        </div>`)
    ];
    this.root.querySelector('[data-zd-steps]').innerHTML = steps.join('');

    const noticeEl = this.root.querySelector('[data-zd-notice]');
    if (noticeEl && this.notice) {
      noticeEl.hidden = false;
      noticeEl.className = `zd-notice zd-notice--${this.notice.kind}`;
      noticeEl.innerHTML = `<span>${esc(this.notice.text)}</span>${this.notice.reset ? '<button type="button" class="zd-link" data-action="reset">Start a new design</button>' : ''}<button type="button" class="zd-notice__close" data-action="dismiss-notice" aria-label="Dismiss">×</button>`;
    }
    this.root.querySelector('[data-zd-toolbar]').innerHTML = `
      <div class="zd-toolbar__group" role="group" aria-label="Camera views">
        ${['perspective', 'front', 'side', 'detail'].map((v) => `<button type="button" class="zd-tool" data-view="${v}" aria-pressed="${v === 'perspective'}">${v === 'detail' ? 'Close-up' : v[0].toUpperCase() + v.slice(1)}</button>`).join('')}
      </div>
      <div class="zd-toolbar__group" role="group" aria-label="Viewer tools">
        <button type="button" class="zd-tool zd-tool--icon" data-action="scene" aria-pressed="false" title="Kitchen scene" aria-label="Toggle the kitchen-inspired scene">${ICON.scene}</button>
        <button type="button" class="zd-tool zd-tool--icon" data-action="reset-view" title="Reset view" aria-label="Reset view">${ICON.rotate}</button>
        <button type="button" class="zd-tool zd-tool--icon" data-action="dims" aria-pressed="true" title="Show dimensions" aria-label="Show dimensions">${ICON.dims}</button>
        <button type="button" class="zd-tool zd-tool--icon" data-action="fullscreen" aria-pressed="false" title="Full screen" aria-label="Full screen preview">${ICON.full}</button>
        <button type="button" class="zd-tool zd-tool--icon" data-action="png" title="Download PNG" aria-label="Download PNG of the current design">${ICON.png}</button>
        <button type="button" class="zd-tool zd-tool--icon zd-tool--mobile" data-action="pin" aria-pressed="false" title="Keep a compact preview visible" aria-label="Keep a compact preview visible while scrolling">${ICON.pin}</button>
      </div>`;
    this.renderMountingCards();
    this.renderPresetCards();
  }

  renderMountingCards() {
    const host = this.root.querySelector('[data-mounting-cards]');
    host.innerHTML = core.mountingsFor(this.state.model).map((mt) =>
      this.cardHTML('mounting', mt.id, mt.name, mt.attach, { thumb: 'mounting:' + mt.id })).join('');
  }
  renderPresetCards() {
    const host = this.root.querySelector('[data-preset-cards]');
    const mt = core.mountingDef(this.state.model, this.state.mounting);
    const handles = mt.arms ? [mt.handles[this.state.arms === 'flat' ? 1 : 0]].filter(Boolean) : mt.handles;
    const configs = [];
    for (const h of handles) {
      const e = this.index.byHandle[h];
      if (e) configs.push(...e.configs);
    }
    configs.sort((a, b) => a.W - b.W || a.H - b.H);
    const u = this.state.units;
    const fl = (inch) => core.formatLength(IN(inch), u);
    host.innerHTML = configs.map((c) => {
      const pid = [c.W, c.H, c.D, c.L, c.B].join('x');
      return `<label class="zd-preset"><input type="radio" name="${this.ids('preset')}" value="${pid}" data-field="preset"><span><strong>${fl(c.W)} × ${fl(c.H)} × ${fl(c.D)}</strong><span class="zd-preset__dims">${c.L} levels · ${c.B} bay${c.B > 1 ? 's' : ''}</span></span></label>`;
    }).join('') + `<label class="zd-preset"><input type="radio" name="${this.ids('preset')}" value="custom" data-field="preset"><span><strong>Custom</strong><span class="zd-preset__dims">Your own size</span></span></label>`;
    this._presetConfigs = configs;
  }

  /* ============================================================ bind */
  bind() {
    const r = this.root;
    r.addEventListener('change', (e) => {
      const t = e.target;
      const f = t.dataset.field;
      if (!f || t.type !== 'radio') return;
      const v = t.value;
      const s = this.state;
      switch (f) {
        case 'units': {
          this.raw = Object.fromEntries(Object.entries(this.raw).filter(([, val]) => !core.parseLengthInput(val, s.units).ok));
          s.units = v;
          this.renderPresetCards();
          return this.update({ announce: `Units set to ${v === 'cm' ? 'centimetres' : 'inches'}` });
        }
        case 'model': {
          s.model = v;
          const mts = core.mountingsFor(v);
          if (!mts.some((m) => m.id === s.mounting)) s.mounting = mts[0].id;
          this.clampToMounting();
          this.renderMountingCards();
          this.renderPresetCards();
          return this.update({ announce: core.MODELS.find((m) => m.id === v)?.name });
        }
        case 'mounting': {
          s.mounting = v;
          this.clampToMounting();
          this.renderPresetCards();
          return this.update({ announce: core.mountingDef(s.model, v)?.name });
        }
        case 'arms': { s.arms = v; this.renderPresetCards(); return this.update({ announce: v === 'flat' ? 'Flat support arms' : 'Turned support arms' }); }
        case 'preset': return this.applyPreset(v);
        case 'gapMode': {
          s.gapMode = v;
          if (v === 'custom' && !(s.gap > 0)) s.gap = IN(r2(core.evenGapIn(s)));
          delete this.raw.G;
          return this.update({ announce: v === 'custom' ? 'Custom shelf spacing' : 'Evenly spaced shelves' });
        }
        case 'shelves': {
          if (v === 'none') { if (s.shelves === 'included') s.lastMaterial = s.material; }
          else s.material = s.lastMaterial || 'hammered';
          s.shelves = v;
          return this.update({ announce: v === 'none' ? 'Frame only — shelf panels removed' : 'Shelf panels included' });
        }
        case 'material': { s.material = v; s.lastMaterial = v; return this.update({ announce: core.SHELF_MATERIALS.find((m) => m.id === v)?.name }); }
        default: { s[f] = v; return this.update({ announce: `${f} updated` }); }
      }
    });
    r.addEventListener('input', (e) => {
      const t = e.target;
      if (t.dataset.dimInput) this.onDimInput(t.dataset.dimInput, t.value);
      if (t.hasAttribute('data-notes')) this.notesText = t.value;
    });
    r.addEventListener('click', (e) => {
      const b = e.target.closest('button');
      if (!b || !r.contains(b)) return;
      if (b.dataset.step) return this.stepValue(b.dataset.step, Number(b.dataset.dir));
      if (b.dataset.view) return this.setView(b.dataset.view);
      if (b.dataset.edit) return this.goToStep(b.dataset.edit);
      if (b.dataset.action) this.action(b.dataset.action, b);
    });
    r.addEventListener('focusin', (e) => { if (e.target.matches('input[type=text]')) r.classList.add('zd--typing'); });
    r.addEventListener('focusout', () => setTimeout(() => { if (!r.contains(document.activeElement) || !document.activeElement.matches('input[type=text]')) r.classList.remove('zd--typing'); }, 50));
    document.addEventListener('fullscreenchange', () => this.onFullscreenChange());
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && r.classList.contains('zd--fs-fallback')) this.toggleFullscreen(false); });
    window.addEventListener('afterprint', () => document.documentElement.classList.remove('zd-is-printing'));
    document.addEventListener('shopify:section:unload', (e) => { if (e.detail && e.detail.sectionId === this.sid) this.destroy(); });
    const inline = r.querySelector('[data-zd-action]');
    if ('IntersectionObserver' in window && inline) {
      new IntersectionObserver((en) => en.forEach((x) => r.classList.toggle('zd--action-visible', x.isIntersecting)), { threshold: 0.2 }).observe(inline);
    }
  }

  clampToMounting() {
    const s = this.state;
    const mt = core.mountingDef(s.model, s.mounting);
    const cl = (v, [lo, hi]) => Math.min(IN(hi), Math.max(IN(lo), v));
    s.dims = { W: cl(s.dims.W, mt.lim.W), H: cl(s.dims.H, mt.lim.H), D: cl(s.dims.D, mt.lim.D) };
    s.levels = Math.min(mt.lim.L[1], Math.max(mt.lim.L[0], s.levels));
    s.bays = Math.min(mt.lim.B[1], Math.max(mt.lim.B[0], s.bays));
    ['W', 'H', 'D'].forEach((k) => delete this.raw[k]);
    s.preset = this.matchPreset();
  }
  applyPreset(pid) {
    const s = this.state;
    if (pid === 'custom') { s.preset = 'custom'; return this.update(); }
    const c = (this._presetConfigs || []).find((x) => [x.W, x.H, x.D, x.L, x.B].join('x') === pid);
    if (!c) return;
    ['W', 'H', 'D'].forEach((k) => delete this.raw[k]);
    s.preset = pid;
    s.dims = { W: IN(c.W), H: IN(c.H), D: IN(c.D) };
    s.levels = c.L; s.bays = c.B; s.gapMode = 'even';
    this.update({ announce: `Standard size applied` });
  }
  matchPreset() {
    const s = this.state;
    const hit = (this._presetConfigs || []).find((c) => Math.abs(IN(c.W) - s.dims.W) < 0.05 && Math.abs(IN(c.H) - s.dims.H) < 0.05 && Math.abs(IN(c.D) - s.dims.D) < 0.05 && c.L === s.levels && c.B === s.bays);
    return hit ? [hit.W, hit.H, hit.D, hit.L, hit.B].join('x') : 'custom';
  }
  writeDim(k, mm) {
    if (k === 'G') { this.state.gap = mm; return; }
    this.state.dims = { ...this.state.dims, [k]: mm };
    this.state.preset = this.matchPreset();
  }
  onDimInput(k, value) {
    this.raw[k] = value;
    const p = core.parseLengthInput(value, this.state.units);
    if (p.ok && (k === 'G' || !core.checkDimension(k, p.mm, this.state, this.config).error)) this.writeDim(k, p.mm);
    this.update({ skipDimInput: k });
  }
  stepValue(k, dir) {
    const s = this.state, u = s.units;
    const inc = u === 'cm' ? 1 : (k === 'D' ? 0.5 : 1);
    const cur = k === 'G' ? s.gap : s.dims[k];
    const raw = this.raw[k];
    const base = raw !== undefined && core.parseLengthInput(raw, u).ok ? Number(String(raw).replace(',', '.')) : core.displayValue(cur, u);
    const next = Math.max(inc, Math.round((base + dir * inc) / inc) * inc);
    delete this.raw[k];
    let mm = core.toMm(next, u);
    if (k !== 'G') {
      const mt = core.mountingDef(s.model, s.mounting);
      const [lo, hi] = mt.lim[k];
      mm = Math.min(IN(hi), Math.max(IN(lo), mm));
    } else {
      mm = Math.min(IN(this.config.gapIn.max), Math.max(IN(this.config.gapIn.min), mm));
    }
    this.writeDim(k, mm);
    this.update({ announce: true });
  }

  /* ============================================================ update (every change) */
  update(opts = {}) {
    const C = this.config, s = this.state, r = this.root, u = s.units;
    const mt = core.mountingDef(s.model, s.mounting);
    this.validation = core.validateState(s, C, this.raw);
    this.pricing = core.priceState(s, C, this.validation, this.index);
    const v = this.validation;
    const L = core.labelsFor(s, C);
    const cur = { units: s.units, model: s.model, mounting: s.mounting, arms: s.arms, preset: s.preset, gapMode: s.gapMode, shelves: s.shelves, material: s.material, finish: s.finish };
    r.querySelectorAll('input[type=radio][data-field]').forEach((inp) => (inp.checked = String(cur[inp.dataset.field]) === inp.value));
    r.querySelectorAll('[data-unit-label]').forEach((el) => (el.textContent = u === 'cm' ? '(cm)' : '(in)'));
    r.querySelector('[data-mounting-note]').textContent = mt.attach;
    r.querySelector('[data-arms-wrap]').hidden = !mt.arms;
    r.querySelector('[data-dim-label="H"]').textContent = mt.hLabel;
    r.querySelector('[data-size-note]').textContent = {
      'counter-ceiling': 'Measure from the finished countertop to the finished ceiling. Width and depth are the outside frame sizes.',
      'floor-ceiling': 'Measure from the finished floor to the finished ceiling. Width and depth are the outside frame sizes.',
      'counter-wall': 'Height is from the countertop to the top of the frame. Depth is from the wall to the front of the frame.',
      'floor-wall': 'Height is from the floor to the top of the frame. Depth is from the wall to the front of the frame.',
      wall: 'Height is the overall frame height on the wall. Depth is from the wall to the front of the frame.',
      'ceiling-wall': 'Height is from the ceiling fixing down to the lowest rail. Depth is from the wall to the front of the frame.',
      ceiling: 'Height is the drop from the ceiling to the lowest rail. The unit hangs clear on every side.',
      freestanding: 'Overall outside sizes of the étagère. We recommend a discreet wall restraint.'
    }[s.mounting] || '';
    // dimension inputs
    ['W', 'H', 'D'].forEach((k) => {
      const inp = r.querySelector(`[data-dim-input="${k}"]`);
      if (opts.skipDimInput !== k) inp.value = this.raw[k] !== undefined ? this.raw[k] : String(core.displayValue(s.dims[k], u));
      const [lo, hi] = mt.lim[k];
      r.querySelector(`[data-hint="${k}"]`).textContent = `${core.formatLength(IN(lo), u)} – ${core.formatLength(IN(hi), u)} for ${mt.name.toLowerCase()}.`;
      this.showError(k, v.errors[k], inp);
    });
    const gInp = r.querySelector('[data-dim-input="G"]');
    r.querySelector('[data-gap-field]').hidden = s.gapMode !== 'custom';
    if (s.gapMode === 'custom' && opts.skipDimInput !== 'G') gInp.value = this.raw.G !== undefined ? this.raw.G : String(core.displayValue(s.gap || IN(core.evenGapIn(s)), u));
    this.showError('gap', v.errors.gap, gInp);
    ['model', 'mounting', 'finish', 'material', 'levels', 'bays'].forEach((k) => this.showError(k, v.errors[k]));
    // levels & bays counters
    r.querySelector('[data-count-val="lv"]').textContent = s.levels;
    r.querySelector('[data-count-val="by"]').textContent = s.bays;
    r.querySelector('[data-count-hint="lv"]').textContent = `${mt.lim.L[0]}–${mt.lim.L[1]} levels for ${mt.name.toLowerCase()}. ${s.gapMode === 'even' ? `Currently about ${core.formatLength(IN(r2(core.evenGapIn(s))), u)} between shelves.` : ''}`;
    r.querySelector('[data-count-hint="by"]').textContent = `Each bay spans ${core.formatLength(s.dims.W / s.bays, u)} between posts (${core.postCount(s)} posts in total).`;
    r.querySelector('[data-structure-note]').textContent = `Support posts: ${core.postCount(s)} (front and back row). Every level has front, back and cross rails.`;
    // shelves / material
    const inc = s.shelves === 'included';
    r.querySelector('[data-material-wrap]').hidden = !inc;
    r.querySelector('[data-frame-only-note]').hidden = inc;
    const n = core.panelCount(core.pricingInput(s));
    r.querySelector('[data-panel-count]').textContent = inc ? `· ${n} panel${n === 1 ? '' : 's'} of ${core.formatLength(s.dims.W / s.bays, u)} × ${core.formatLength(s.dims.D, u)}` : '';
    r.querySelector('[data-material-note]').textContent = inc ? {
      hammered: 'Hand-hammered solid brass sheet over a solid wooden core — hammered in our workshop, in the finish you chose in step 5.',
      glass: 'Clear glass with polished edges. Exact glass thickness and specification are confirmed at the detail check before production.',
      wood: 'Solid wood with an oiled finish; species is confirmed at the detail check. Grain runs along the shelf length.'
    }[s.material] : '';

    const sum = {
      model: L.model,
      mounting: L.mounting + (mt.arms && s.arms === 'flat' ? ' · flat arms' : ''),
      size: `${L.width} × ${L.height} × ${L.depth}`,
      levels: `${s.levels} × ${s.bays}`,
      finish: L.finish,
      shelves: s.shelves === 'none' ? 'Frame only' : core.SHELF_MATERIALS.find((m) => m.id === s.material)?.name,
      review: core.designRef(s)
    };
    r.querySelectorAll('[data-summary]').forEach((el) => (el.textContent = sum[el.dataset.summary] || ''));
    this.sum = sum;
    this.renderDeltas();
    this.renderFinishDetail();
    this.renderReview(L);
    this.renderAction(L, sum);
    this.renderIncluded();
    this.updatePreview();
    this.queueThumbs();
    this.save();
    if (opts.announce && !opts.initial) this.announce(`${typeof opts.announce === 'string' ? opts.announce + '. ' : ''}${sum.size}. ${this.totalText()}`);
  }

  showError(key, msg, input) {
    const el = this.root.querySelector(`[data-error="${key}"]`);
    if (!el) return;
    el.hidden = !msg;
    el.textContent = msg || '';
    if (input) input.setAttribute('aria-invalid', msg ? 'true' : 'false');
    const field = el.closest('.zd-field');
    if (field) field.classList.toggle('is-invalid', !!msg);
  }
  renderFinishDetail() {
    const f = this.config.finishes.find((x) => x.id === this.state.finish);
    const el = this.root.querySelector('[data-finish-detail]');
    if (!f) { el.innerHTML = ''; return; }
    el.innerHTML = `<div class="zd-finish-detail__swatch" style="--zd-swatch:${esc(f.color)}" aria-hidden="true"></div>
      <div class="zd-finish-detail__text"><h3 class="zd-h3">${esc(f.name)}</h3><p>${esc(f.description)}</p>
      ${f.patina ? `<p><strong>Over time:</strong> ${esc(f.patina)}</p>` : ''}${f.care ? `<p><strong>Care:</strong> ${esc(f.care)}</p>` : ''}</div>`;
  }
  renderReview(L) {
    const s = this.state;
    const rows = [['model', 'Model', L.model], ['mounting', 'Mounting', L.mounting + (core.mountingDef(s.model, s.mounting).arms ? ` — ${L.arms.toLowerCase()}` : '')],
      ['size', 'Overall width', L.width], ['size', L.hLabel, L.height], ['size', 'Depth', L.depth],
      ['levels', 'Shelf levels', `${s.levels}`], ['levels', 'Bays', `${s.bays}`], ['levels', 'Shelf spacing', L.spacing],
      ['finish', 'Finish', L.finish], ['shelves', 'Shelf panels', L.shelves]];
    this.root.querySelector('[data-review]').innerHTML = rows.map(([st, k, val]) => `
      <div class="zd-review__row"><dt>${esc(k)}</dt><dd>${esc(val)}</dd><dd class="zd-review__edit"><button type="button" class="zd-link" data-edit="${st}">Edit<span class="zd-visually-hidden"> ${esc(k.toLowerCase())}</span></button></dd></div>`).join('');
    this.root.querySelector('[data-included]').innerHTML = core.includedComponents(s, this.config).map((x) => `<li>${esc(x)}</li>`).join('');
    this.root.querySelector('[data-excluded]').innerHTML = core.excludedFor(s).map((x) => `<li>${esc(x)}</li>`).join('');
    this.root.querySelector('[data-pricebox]').innerHTML = `<h3 class="zd-h3">Price summary</h3>${this.breakdownHTML()}`;
    this.root.querySelector('[data-warnings]').innerHTML = this.validation.warnings.map((w) => `<li>${esc(w)}</li>`).join('');
  }

  /* ------------------------------------------------------------ money */
  money(c) { const { shop, active, rate } = this.currency; return active === shop ? core.formatMoney(c, this.config.pricing.moneyFormat) : this.moneyActive(Math.round(c * rate)); }
  moneyActive(c) {
    const { shop, active } = this.currency;
    if (active === shop) return core.formatMoney(c, this.config.pricing.moneyFormat);
    try { return new Intl.NumberFormat(undefined, { style: 'currency', currency: active }).format(c / 100); } catch (e) { return `${(c / 100).toFixed(2)} ${active}`; }
  }
  unitChargeCents() { return this.pricing.variant ? Number(this.pricing.variant.shopCents ?? this.pricing.variant.price) : null; }
  totalText() {
    const p = this.pricing;
    if (p.mode === 'exact' || p.mode === 'direct') return this.moneyActive(this.unitChargeCents() * this.qty);
    if (p.mode === 'invalid') return 'Check highlighted fields';
    return p.unitCents == null ? 'Contact us for a quote' : 'Unavailable';
  }
  /** Display lines: for exact catalog matches, split frame vs panels from the catalog itself. */
  displayLines() {
    const p = this.pricing, s = this.state;
    if (p.mode !== 'exact') return p.lines;
    const e = this.index.byHandle[p.handle];
    const key = [r2(s.dims.W / core.MM_PER_IN), r2(s.dims.H / core.MM_PER_IN), r2(s.dims.D / core.MM_PER_IN), s.levels, s.bays].join('x');
    const group = e && e.variants[key];
    const frameV = group && group.none && group.none[s.finish];
    const lines = [];
    if (frameV && s.shelves === 'included') {
      const frame = Number(frameV.price);
      const n = core.panelCount(core.pricingInput(s));
      const mat = core.SHELF_MATERIALS.find((m) => m.id === s.material);
      lines.push({ key: 'base', label: 'Brass frame', cents: frame });
      lines.push({ key: 'shelf', label: `${n} ${mat?.name.toLowerCase()} shelf panel${n === 1 ? '' : 's'}`, cents: p.unitCents - frame });
    } else {
      lines.push({ key: 'base', label: 'Brass frame', cents: p.unitCents });
    }
    return lines;
  }
  breakdownHTML() {
    const p = this.pricing;
    if (p.mode === 'invalid') return `<p class="zd-small">Correct the highlighted ${Object.keys(this.validation.errors).length > 1 ? 'fields' : 'field'} to see your price.</p>`;
    const L = core.labelsFor(this.state, this.config);
    const lines = this.displayLines();
    const rows = lines.map((l) => `<tr><th scope="row">${l.key === 'base' ? `Brass frame · ${esc(L.width)} × ${esc(L.height)} × ${esc(L.depth)}<span class="zd-breakdown__sub">${esc(L.model)}, ${esc(L.mounting.toLowerCase())}, ${this.state.levels} levels × ${this.state.bays} bay${this.state.bays > 1 ? 's' : ''}, ${esc(L.finish.toLowerCase())}</span>` : esc(l.label)}</th><td>${l.key === 'base' ? '' : '+ '}${this.money(l.cents)}</td></tr>`).join('');
    const unit = this.unitChargeCents();
    const src = p.mode === 'exact'
      ? 'This configuration matches a Kinycraft catalog listing, so you pay that listing’s exact price.'
      : 'This is the exact product price charged in your cart and at checkout.';
    return `<table class="zd-breakdown"><tbody>${rows}
      <tr class="zd-breakdown__unit"><th scope="row">Price per unit</th><td>${unit != null ? this.moneyActive(unit) : '—'}</td></tr>
      <tr><th scope="row">Quantity</th><td>× ${this.qty}</td></tr>
      <tr class="zd-breakdown__total"><th scope="row">Total</th><td>${this.totalText()}</td></tr></tbody></table>
      <p class="zd-small zd-muted">${src} Shipping and any taxes are calculated at checkout.${this.currency.active !== this.currency.shop ? ' Line amounts are converted for display; the unit price and total are exact.' : ''}</p>`;
  }
  renderDeltas() {
    const s = this.state;
    const els = this.root.querySelectorAll('[data-delta]');
    if (!this.validation.valid) { els.forEach((el) => (el.textContent = '')); return; }
    const price = (t) => {
      const val = core.validateState(t, this.config);
      if (!val.valid) return null;
      const pr = core.priceState(t, this.config, val, this.index);
      return pr.unitCents ?? core.unitPrice(t, this.index);
    };
    const withVal = (field, value) => {
      const t = structuredClone(s);
      if (field === 'model') { t.model = value; const mts = core.mountingsFor(value); if (!mts.some((m) => m.id === t.mounting)) t.mounting = mts[0].id; const mt = core.mountingDef(t.model, t.mounting); const cl = (v2, [lo, hi]) => Math.min(IN(hi), Math.max(IN(lo), v2)); t.dims = { W: cl(t.dims.W, mt.lim.W), H: cl(t.dims.H, mt.lim.H), D: cl(t.dims.D, mt.lim.D) }; t.levels = Math.min(mt.lim.L[1], Math.max(mt.lim.L[0], t.levels)); t.bays = Math.min(mt.lim.B[1], Math.max(mt.lim.B[0], t.bays)); }
      else if (field === 'mounting') { t.mounting = value; const mt = core.mountingDef(t.model, value); const cl = (v2, [lo, hi]) => Math.min(IN(hi), Math.max(IN(lo), v2)); t.dims = { W: cl(t.dims.W, mt.lim.W), H: cl(t.dims.H, mt.lim.H), D: cl(t.dims.D, mt.lim.D) }; t.levels = Math.min(mt.lim.L[1], Math.max(mt.lim.L[0], t.levels)); t.bays = Math.min(mt.lim.B[1], Math.max(mt.lim.B[0], t.bays)); }
      else if (field === 'shelves') { t.shelves = value; if (value === 'included') t.material = t.lastMaterial || 'hammered'; }
      else if (field === 'material') { t.shelves = 'included'; t.material = value; }
      else t[field] = value;
      return price(t);
    };
    const current = this.unitChargeCents() ?? this.pricing.unitCents;
    els.forEach((el) => {
      const [field, value] = el.dataset.delta.split(':');
      const isCur = String({ model: s.model, mounting: s.mounting, shelves: s.shelves, material: s.shelves === 'included' ? s.material : '', finish: s.finish, arms: s.arms }[field]) === value;
      if (isCur) { el.textContent = 'Selected'; el.classList.remove('is-extra'); return; }
      const c = withVal(field, value);
      if (c == null || current == null) { el.textContent = ''; return; }
      const d = c - current;
      el.textContent = d > 0 ? `+ ${this.money(d)}` : d < 0 ? `− ${this.money(-d)}` : 'Same price';
      el.classList.toggle('is-extra', d > 0);
    });
  }
  renderAction(L, sum) {
    const p = this.pricing;
    const errCount = Object.keys(this.validation.errors).length;
    const summary = esc([L.model, sum.size, `${this.state.levels} levels`, L.finish].join(' · ')) + (this.state.shelves === 'none' ? ' · frame only' : ' · ' + esc(sum.shelves) + ' shelves');
    const buyable = p.mode === 'exact' || p.mode === 'direct';
    const addBtn = buyable
      ? `<button type="button" class="zd-btn zd-btn--primary zd-btn--add" data-action="add-to-cart"><span class="zd-btn__label">Add to Cart — ${this.totalText()}</span><span class="zd-spinner" aria-hidden="true"></span></button>`
      : p.mode === 'invalid'
        ? `<button type="button" class="zd-btn zd-btn--primary" data-action="first-error">Fix ${errCount} highlighted ${errCount === 1 ? 'field' : 'fields'}</button>`
        : `<a class="zd-btn zd-btn--primary" href="/pages/contact">Contact us for a quote</a>`;
    const priceBlock = `<div class="zd-price"><span class="zd-price__label">Total${this.qty > 1 ? ` · ${this.qty} units` : ''}</span><span class="zd-price__amount${p.mode === 'invalid' ? ' zd-price__amount--muted' : ''}">${this.totalText()}</span></div>`;
    const qty = `<div class="zd-qty"><span class="zd-field__label" id="${this.ids('qty-l')}">Quantity</span><div class="zd-stepper zd-stepper--sm" role="group" aria-labelledby="${this.ids('qty-l')}"><button type="button" class="zd-stepper__btn" data-action="qty-" aria-label="Decrease quantity">${ICON.minus}</button><output class="zd-qty__val" aria-live="polite">${this.qty}</output><button type="button" class="zd-stepper__btn" data-action="qty+" aria-label="Increase quantity">${ICON.plus}</button></div></div>`;
    const scope = `<p class="zd-scope">${this.state.shelves === 'none' ? 'Brass frame only — shelf panels are not included.' : 'Brass frame with made-to-size shelf panels.'} Wall and ceiling anchors and installation are not included.</p>`;
    const inline = this.root.querySelector('[data-zd-action]');
    const barEl = this.root.querySelector('[data-zd-bar]');
    const stageEl = this.root.querySelector('[data-zd-stagebar]');
    if (this._lastMode !== p.mode) this._cartErr = '';
    this._lastMode = p.mode;
    inline.innerHTML = `<h2 class="zd-action__title">Your bistro shelving</h2><p class="zd-action__summary">${summary}</p>${this.breakdownHTML()}${qty}${scope}
      <div class="zd-action__ctas">${addBtn}${buyable ? '<button type="button" class="zd-btn zd-btn--secondary" data-action="buy-now"><span class="zd-btn__label">Buy it now</span><span class="zd-spinner" aria-hidden="true"></span></button>' : ''}</div>
      <p class="zd-action__ref zd-small zd-muted">Configuration ID <strong>${core.designRef(this.state)}</strong></p><div class="zd-action__error" data-cart-error role="alert" hidden></div>`;
    if (stageEl) stageEl.innerHTML = `<div class="zd-stagebar__info">${priceBlock}<p class="zd-action__summary">${summary}</p></div><div class="zd-action__ctas">${addBtn}</div>${scope}<div class="zd-action__error" data-cart-error role="alert" hidden></div>`;
    if (barEl) barEl.innerHTML = `<div class="zd-price"><span class="zd-price__label">${this.qty > 1 ? `${this.qty} units` : 'Your shelving'}</span><span class="zd-price__dims">${esc(sum.size)}</span></div><div class="zd-action__ctas">${addBtn}</div>`;
    if (this._cartErr) this.showCartError(this._cartErr);
    if (this.busy) this.root.querySelectorAll('[data-action="add-to-cart"],[data-action="buy-now"]').forEach((b) => b.classList.add('is-loading'));
    const notes = this.root.querySelector('[data-notes]');
    if (notes && notes.value !== this.notesText) notes.value = this.notesText;
  }
  renderIncluded() {
    const items = core.includedComponents(this.state, this.config);
    document.querySelectorAll('[data-zd-included-target]').forEach((el) => {
      el.innerHTML = `<ul class="zd-list zd-list--check">${items.map((x) => `<li>${esc(x)}</li>`).join('')}</ul><p class="zd-scope">Not included: ${core.excludedFor(this.state).map((x) => esc(x.toLowerCase())).join('; ')}.</p>`;
    });
  }

  /* ============================================================ preview */
  currentModel(state = this.state) {
    const f = this.config.finishes.find((x) => x.id === state.finish);
    return core.bistroModel(state, this.config, f);
  }
  updatePreview() {
    const m = this.currentModel();
    const svgHost = this.root.querySelector('[data-zd-fallback-svg]');
    if (svgHost && this.viewerMode !== 'webgl') svgHost.innerHTML = core.elevationSVG(m, { stroke: m.finish.color });
    if (this.viewer && this.validation.valid) this.viewer.update(m);
  }
  async initViewer() {
    const host = this.root.querySelector('[data-zd-canvas]');
    const status = this.root.querySelector('[data-zd-viewer-status]');
    const fallback = (reason) => { this.viewerMode = '2d'; this.root.classList.add('zd--2d'); status.hidden = false; status.textContent = reason; this.updatePreview(); this.thumbFallback(); };
    const supportsGL = (() => { try { const c = document.createElement('canvas'); return !!(window.WebGLRenderingContext && (c.getContext('webgl2') || c.getContext('webgl'))); } catch (e) { return false; } })();
    if (!supportsGL) return fallback('3D preview is not available on this device, so we are showing a dimensioned drawing. All options still work.');
    const A = this.config.assets;
    if (!A.three || !A.viewer) return fallback('3D preview is not configured. Showing a dimensioned drawing.');
    status.hidden = false;
    status.textContent = 'Loading 3D preview…';
    const go = async () => {
      try {
        const [THREE, mod] = await Promise.all([import(A.three), import(A.viewer)]);
        this.THREE = THREE; this.mod3d = mod;
        this.viewer = mod.createViewer(host, THREE, { onError: () => { this.destroyViewer(); fallback('The 3D preview stopped responding, so we switched to a dimensioned drawing.'); } });
        this.viewerMode = 'webgl';
        this.root.classList.add('zd--3d');
        status.hidden = true;
        this.viewer.update(this.currentModel());
        if (this.dimsOn === false) this.viewer.setDims(false);
        this.queueThumbs(true);
      } catch (e) {
        console.error('[zd-bistro] 3D viewer failed', e);
        fallback('The 3D preview could not load, so we are showing a dimensioned drawing. All options still work.');
      }
    };
    (window.requestIdleCallback || ((cb) => setTimeout(cb, 120)))(go, { timeout: 1500 });
  }

  /* ------------------------------------------------------------ 3D option thumbnails */
  thumbJobs() {
    const s = this.state;
    const mk = (patch = {}) => {
      const t = structuredClone(s);
      t.units = 'in';
      Object.assign(t, patch);
      const mt = core.mountingDef(t.model, t.mounting);
      const cl = (v, [lo, hi]) => Math.min(IN(hi), Math.max(IN(lo), v));
      t.dims = { W: cl(IN(patch.Wi || 30), mt.lim.W), H: cl(IN(patch.Hi || 42), mt.lim.H), D: cl(IN(patch.Di || 10), mt.lim.D) };
      t.levels = Math.min(mt.lim.L[1], Math.max(mt.lim.L[0], patch.Li || 3));
      t.bays = Math.min(mt.lim.B[1], Math.max(mt.lim.B[0], patch.Bi || 1));
      t.gapMode = 'even';
      return this.currentModel(t);
    };
    const jobs = [];
    // model cards: close-up of one junction so the connector reads clearly
    core.MODELS.forEach((m) => {
      const mounting = core.mountingsFor(m.id)[0].id;
      jobs.push({ key: 'model:' + m.id, model: mk({ model: m.id, mounting, shelves: 'included', material: s.shelves === 'none' ? 'glass' : s.material, Wi: 30, Hi: 48, Di: 10, Li: 3, Bi: 1 }), view: { theta: 0.5, phi: 1.18, fov: 24, target: [13, 34, 9], radius: 26 } });
    });
    // mounting cards: whole small frame
    core.mountingsFor(s.model).forEach((mt) => {
      jobs.push({ key: 'mounting:' + mt.id, model: mk({ mounting: mt.id, shelves: 'included', material: s.shelves === 'none' ? 'glass' : s.material, Wi: 30, Hi: 54, Di: 10, Li: 3, Bi: 1 }), view: { theta: 0.42, phi: 1.2, fov: 30, target: [0, 26, 5], radius: 120 } });
    });
    // material cards: close-up of a shelf corner
    core.SHELF_MATERIALS.forEach((m) => {
      jobs.push({ key: 'material:' + m.id, model: mk({ shelves: 'included', material: m.id, Wi: 24, Hi: 36, Di: 10, Li: 2, Bi: 1 }), view: { theta: 0.65, phi: 1.28, fov: 24, target: [10, 20, 9], radius: 20 } });
    });
    return jobs;
  }
  queueThumbs(now) {
    if (!this.THREE) return;
    const s = this.state;
    const sig = JSON.stringify([s.finish, s.model, s.material, s.shelves]);
    if (sig === this._thumbSig) return;
    clearTimeout(this._thumbT);
    this._thumbT = setTimeout(async () => {
      if (this._thumbBusy) { this._thumbSig = null; return this.queueThumbs(); }
      this._thumbBusy = true;
      this._thumbSig = sig;
      try {
        const all = this.thumbJobs();
        const jobs = all.filter((j) => this.thumbs[j.key] !== sig);
        if (!this.viewer) throw new Error('no viewer');
        const out = await this.viewer.thumbs(jobs);
        Object.entries(out).forEach(([k, url]) => {
          this.thumbs[k] = sig;
          this.root.querySelectorAll(`[data-thumb="${CSS.escape(k)}"]`).forEach((el) => { el.innerHTML = `<img src="${url}" alt="" decoding="async">`; el.classList.add('is-3d'); });
        });
      } catch (e) { console.warn('[zd-bistro] thumbnails failed', e); this.thumbFallback(); }
      this._thumbBusy = false;
    }, now ? 50 : 700);
  }
  thumbFallback() { this.root.querySelectorAll('.zd-thumb:not(.is-3d)').forEach((el) => el.classList.add('is-empty')); }
  destroyViewer() { if (this.viewer) { try { this.viewer.dispose(); } catch (e) { /* ignore */ } this.viewer = null; this.root.classList.remove('zd--3d'); } }
  destroy() { this.destroyViewer(); }
  setView(name) { this.root.querySelectorAll('[data-view]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.view === name))); if (this.viewer) this.viewer.setView(name); }
  toggleFullscreen(force) {
    const el = this.root.querySelector('[data-zd-viewer]');
    const isFs = document.fullscreenElement === el || this.root.classList.contains('zd--fs-fallback');
    const want = force === undefined ? !isFs : force;
    if (want) { if (el.requestFullscreen) el.requestFullscreen().catch(() => this.root.classList.add('zd--fs-fallback')); else this.root.classList.add('zd--fs-fallback'); }
    else { if (document.fullscreenElement) document.exitFullscreen(); this.root.classList.remove('zd--fs-fallback'); }
    setTimeout(() => this.onFullscreenChange(), 60);
  }
  onFullscreenChange() {
    const el = this.root.querySelector('[data-zd-viewer]');
    const on = document.fullscreenElement === el || this.root.classList.contains('zd--fs-fallback');
    this.root.querySelector('[data-action="fullscreen"]')?.setAttribute('aria-pressed', String(on));
    el.classList.toggle('is-fullscreen', on);
    if (this.viewer) this.viewer.resize();
  }
  caption() { const L = core.labelsFor(this.state, this.config); return `${core.designRef(this.state)} · ${L.model} · ${L.width} × ${L.height} × ${L.depth} · ${L.finish} · ${this.config.brand}`; }
  async previewPNG() {
    if (this.viewer) return this.viewer.snapshot({ caption: this.caption() });
    const svg = core.elevationSVG(this.currentModel(), { stroke: this.currentModel().finish.color });
    const img = new Image();
    const url = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml' }));
    await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = url; });
    const c = document.createElement('canvas');
    c.width = 1600; c.height = Math.round(1600 * (img.height / img.width || 0.6)) + 60;
    const ctx = c.getContext('2d');
    ctx.fillStyle = '#f6f1e8'; ctx.fillRect(0, 0, c.width, c.height);
    ctx.drawImage(img, 0, 0, 1600, c.height - 60);
    ctx.fillStyle = '#2b2925'; ctx.font = '500 24px system-ui, sans-serif'; ctx.fillText(this.caption(), 24, c.height - 24);
    URL.revokeObjectURL(url);
    return c.toDataURL('image/png');
  }

  /* ============================================================ actions */
  async action(a, btn) {
    const s = this.state;
    const mt = core.mountingDef(s.model, s.mounting);
    switch (a) {
      case 'reset-view': this.setView('perspective'); break;
      case 'dims': this.dimsOn = btn.getAttribute('aria-pressed') !== 'true'; btn.setAttribute('aria-pressed', String(this.dimsOn)); if (this.viewer) this.viewer.setDims(this.dimsOn); break;
      case 'scene': {
        s.scene = s.scene === 'kitchen' ? 'studio' : 'kitchen';
        btn.setAttribute('aria-pressed', String(s.scene === 'kitchen'));
        if (this.viewer) this.viewer.setScene(s.scene);
        this.save();
        this.announce(s.scene === 'kitchen' ? 'Kitchen scene' : 'Studio scene');
        break;
      }
      case 'fullscreen': this.toggleFullscreen(); break;
      case 'pin': { const on = !this.root.classList.contains('zd--pinned'); this.root.classList.toggle('zd--pinned', on); btn.setAttribute('aria-pressed', String(on)); if (this.viewer) setTimeout(() => this.viewer.resize(), 50); break; }
      case 'png': try { const data = await this.previewPNG(); const l = document.createElement('a'); l.href = data; l.download = `bistro-shelving-${core.designRef(s)}.png`; document.body.appendChild(l); l.click(); l.remove(); } catch (e) { this.announce('Sorry, the image could not be created.'); } break;
      case 'share': { const box = this.root.querySelector('[data-share]'); const url = this.shareURL(); box.hidden = false; box.querySelector('[data-share-url]').value = url; await this.copy(url); break; }
      case 'copy': await this.copy(this.root.querySelector('[data-share-url]').value); break;
      case 'print': await this.print(); break;
      case 'reset': this.confirmReset(); break;
      case 'dismiss-notice': this.root.querySelector('[data-zd-notice]').hidden = true; break;
      case 'first-error': this.focusFirstError(); break;
      case 'qty-': this.qty = Math.max(1, this.qty - 1); this.update(); break;
      case 'qty+': if (this.qty >= this.config.pricing.maxDirectQty) { this.showCartError(`You can order up to ${this.config.pricing.maxDirectQty} of one configuration at a time.`); break; } this.qty += 1; this.update(); break;
      case 'lv-': s.levels = Math.max(mt.lim.L[0], s.levels - 1); s.preset = this.matchPreset(); this.update({ announce: `${s.levels} shelf levels` }); break;
      case 'lv+': s.levels = Math.min(mt.lim.L[1], s.levels + 1); s.preset = this.matchPreset(); this.update({ announce: `${s.levels} shelf levels` }); break;
      case 'by-': s.bays = Math.max(mt.lim.B[0], s.bays - 1); s.preset = this.matchPreset(); this.update({ announce: `${s.bays} bays` }); break;
      case 'by+': s.bays = Math.min(mt.lim.B[1], s.bays + 1); s.preset = this.matchPreset(); this.update({ announce: `${s.bays} bays` }); break;
      case 'add-to-cart': await this.addToCart(false); break;
      case 'buy-now': await this.addToCart(true); break;
      default: break;
    }
  }
  shareURL() { const u = new URL(location.href); u.search = ''; u.hash = ''; u.searchParams.set('design', core.encodeShare(this.state)); return u.toString(); }
  async copy(text) {
    try { await navigator.clipboard.writeText(text); this.announce('Share link copied', true); }
    catch (e) { const inp = this.root.querySelector('[data-share-url]'); if (inp) { inp.focus(); inp.select(); } this.announce('Select and copy the link shown.', true); }
  }
  confirmReset() {
    const dlg = this.root.querySelector('[data-zd-reset-dialog]');
    const doReset = () => {
      safeStorage.del(STORAGE_KEY);
      this.raw = {}; this.qty = 1; this.notesText = '';
      this.state = core.defaultState(this.config);
      const n = this.root.querySelector('[data-zd-notice]'); if (n) n.hidden = true;
      if (location.search.includes('design=')) history.replaceState(null, '', location.pathname);
      this.renderMountingCards();
      this.renderPresetCards();
      this.update({ announce: 'Design reset to defaults' });
      if (this.viewer) this.viewer.reset();
    };
    if (dlg && typeof dlg.showModal === 'function') { dlg.showModal(); dlg.addEventListener('close', () => { if (dlg.returnValue === 'confirm') doReset(); }, { once: true }); }
    else if (window.confirm('Reset your design?')) doReset();
  }
  goToStep(key) {
    const el = this.root.querySelector(`#${this.ids('step-' + key)}`);
    if (!el) return;
    el.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
    const f = el.querySelector('input:checked, input:not([type=hidden]), button');
    if (f) setTimeout(() => f.focus({ preventScroll: true }), 300);
  }
  focusFirstError() {
    const err = this.root.querySelector('[data-zd-steps] .zd-field__error:not([hidden])');
    if (!err) return;
    const field = err.closest('.zd-field, .zd-fieldset, .zd-step');
    const inp = field && field.querySelector('input');
    (inp || err).scrollIntoView({ block: 'center' });
    if (inp) inp.focus({ preventScroll: true });
  }
  announce(msg, assertive) {
    const el = this.root.querySelector(assertive ? '[data-zd-live-assertive]' : '[data-zd-live]');
    if (!el) return;
    clearTimeout(this._annT);
    this._annT = setTimeout(() => { el.textContent = ''; requestAnimationFrame(() => (el.textContent = msg)); }, assertive ? 0 : 400);
  }
  async print() {
    let img = '';
    try { img = `<img src="${await this.previewPNG()}" alt="Preview of bistro shelving design">`; } catch (e) { /* drawing only */ }
    let host = document.querySelector('body > .zd-print-root');
    if (!host) { host = document.createElement('div'); host.className = 'zd-print zd-print-root'; host.setAttribute('aria-hidden', 'true'); document.body.appendChild(host); }
    host.innerHTML = `<h1>${esc(this.config.brand)} — Custom Brass Bistro Shelving</h1>
      <p>Design reference <strong>${core.designRef(this.state)}</strong> · ${new Date().toLocaleDateString()}</p>
      <div class="zd-print__media">${img}<div class="zd-print__svg">${core.elevationSVG(this.currentModel(), { stroke: '#8a6a32' })}</div></div>
      <table>${this.propRows().map(([k, v]) => `<tr><th>${esc(k)}</th><td>${esc(v)}</td></tr>`).join('')}<tr><th>Price</th><td>${esc(this.totalText())}</td></tr></table>
      <h2>Included</h2><ul>${core.includedComponents(this.state, this.config).map((x) => `<li>${esc(x)}</li>`).join('')}</ul>
      <h2>Not included</h2><ul>${core.excludedFor(this.state).map((x) => `<li>${esc(x)}</li>`).join('')}</ul>
      <p class="zd-print__link">Reopen this design: ${esc(this.shareURL())}</p>`;
    document.documentElement.classList.add('zd-is-printing');
    setTimeout(() => window.print(), 50);
  }

  /* ============================================================ cart */
  propRows() {
    const L = core.labelsFor(this.state, this.config), s = this.state;
    const mt = core.mountingDef(s.model, s.mounting);
    return [['Model', `${L.model} connectors`],
      ['Mounting', L.mounting + (mt.arms ? ` — ${L.arms.toLowerCase()}` : '')],
      ['Overall width', core.formatLengthBoth(s.dims.W)],
      [L.hLabel, core.formatLengthBoth(s.dims.H)],
      ['Depth', core.formatLengthBoth(s.dims.D)],
      ['Shelf levels', String(s.levels)], ['Bays', String(s.bays)],
      ['Shelf spacing', L.spacing],
      ['Brass finish', L.finish],
      ['Shelf panels', s.shelves === 'none' ? 'Not included (frame only)' : `Included — ${L.shelves}`]];
  }
  cartProperties() {
    const p = this.pricing;
    const props = { 'Configuration ID': core.designRef(this.state) };
    this.propRows().forEach(([k, v]) => (props[k] = v));
    props['Units chosen'] = this.state.units === 'cm' ? 'Centimetres' : 'Inches';
    if (this.notesText.trim()) props['Customer notes'] = this.notesText.trim().slice(0, 500);
    props._Components = core.includedComponents(this.state, this.config).join('; ');
    props['_Price basis'] = p.mode === 'exact' ? `Catalog variant (${p.handle})` : `Derived from catalog anchors, pricing v${PRICING.version}`;
    props['_Price breakdown'] = this.displayLines().map((l) => `${l.label}: ${(l.cents / 100).toFixed(2)}`).join(' | ') + ` | Unit: ${(p.unitCents / 100).toFixed(2)} ${this.currency.shop}`;
    props['_Design link'] = this.shareURL();
    props._Configuration = JSON.stringify(core.compactState(this.state));
    return props;
  }
  showCartError(msg) { this._cartErr = msg || ''; this.root.querySelectorAll('[data-cart-error]').forEach((el) => { el.hidden = !msg; el.textContent = msg || ''; }); }
  async addToCart(buyNow) {
    if (this.busy) return;
    this.validation = core.validateState(this.state, this.config, this.raw);
    this.pricing = core.priceState(this.state, this.config, this.validation, this.index);
    if (this.pricing.mode !== 'exact' && this.pricing.mode !== 'direct') {
      this.update();
      this.showCartError(this.pricing.mode === 'invalid' ? 'Please correct the highlighted fields first.' : 'This configuration cannot be added to the cart right now. Please contact us for a quote.');
      return;
    }
    const variant = this.pricing.variant;
    const expected = Number(variant.shopCents ?? variant.price);
    this.busy = true;
    this.showCartError('');
    this.root.querySelectorAll('[data-action="add-to-cart"],[data-action="buy-now"]').forEach((b) => { b.classList.add('is-loading'); b.setAttribute('aria-disabled', 'true'); });
    try {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      // Only a variant id and quantity are sent; Shopify charges that variant's own price.
      const res = await fetch(`${root}cart/add.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [{ id: Number(variant.id), quantity: this.qty, properties: this.cartProperties() }] }) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.description || data.message || 'We could not add this shelving to your cart. Please try again.');
      const item = (data.items && data.items[0]) || data;
      if (item && typeof item.price === 'number' && item.price !== expected) { this.showCartError('The cart returned a different price for this design. Please refresh the page before checking out.'); return; }
      this.announce('Bistro shelving added to your cart', true);
      if (buyNow) { location.href = `${root}checkout`; return; }
      if (this.config.cart?.afterAdd !== 'cart' && document.querySelector('cart-drawer')) {
        document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: true } }));
        document.dispatchEvent(new CustomEvent('product-ajax:added', { detail: { product: data } }));
      } else location.href = `${root}cart`;
    } catch (e) { this.showCartError(e.message || 'Something went wrong. Please try again.'); }
    finally {
      this.busy = false;
      this.root.querySelectorAll('[data-action="add-to-cart"],[data-action="buy-now"]').forEach((b) => { b.classList.remove('is-loading'); b.removeAttribute('aria-disabled'); });
    }
  }
}

function init() {
  document.querySelectorAll('[data-zd-bistro-builder]:not([data-zd-ready])').forEach((el) => { el.setAttribute('data-zd-ready', ''); el.__zdBuilder = new BistroBuilder(el); });
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
document.addEventListener('shopify:section:load', init);
